import moment from 'moment-timezone'

export const processTimezone = (tz) => ({
  id: tz.id,
  name: tz.name,
})

export const processUser = (user) => ({
  id: user.id,
  createTime: user.create_time,
  updateTime: user.update_time,
  firstName: user.first_name,
  lastName: user.last_name,
  username: user.username,
  groups: user.groups || [],
  instanceId: user.instance_id || 0,
})

export const processInstance = (instance) => ({
  id: instance.id,
  extId: instance.ext_id,
  name: instance.name,
  description: instance.description,
})

export const processChannel = (channel) => {
  const processed = {
    id: channel.id,
    createTime: channel.create_time,
    updateTime: channel.update_time,
    name: channel.name,
    description: channel.description,
    shortDescription: channel.short_description || '',
    fccRating: channel.fcc_rating,
    logoId: channel.logo_id,
    logo: '',
    dvrDuration: channel.np_dvr_duration || 0,
    streamURLhlsv3: channel.stream_hls3_url,
    streamURLhlsv5: channel.stream_hls5_url,
    streamURL: '',
    streamFrameSrc: '',
    ssaiURL: channel.ssai_url,
    adDuration: channel.ad_duration || 0,
    autoScheduler: channel.auto_scheduler,
    autoSchedulerAdInterval: channel.auto_scheduler_ad_interval,
    autoSchedulerTags: [],
    autoSchedulerRules: [],
    autoSchedulerSettings: channel.auto_scheduler_settings || {},
    autoSchedulerDepth: 0,
    watermark: {
      position: 'topLeft',
      size: '200',
    },
    watermarkPictureId: 0,
    watermarkPicture: '',
    splashScreenId: channel.splash_screen_id,
    splashScreen: '',
    layout: {},
    timezone: {},
    templates: [],
    currentProgram: {},
    xmltv: {
      lastImportTimeAuto: '',
      lastImportTimeCustom: '',
      resultURLAuto: '',
      resultURLCustom: '',
      userOutputURL: '',
    },
    dummyEPG: [],
    resultXMLTVURL: '',
    xmltvOutputURL: '',
    ads: {
      inContentAdvertising: false,
      adBlockDuration: 0,
      minInterval: 0,
      programSafeZoneLeft: 0,
      programSafeZoneRight: 0,
    },
    version: channel.version || 1,
    suspended: channel.suspended || false,
    instance: {},
    genres: channel.genres || [],
    rating: channel.rating || '',
    USPrivacy: channel.us_privacy || '',
    IABCategory: channel.iab_category || '',
    denyAll: '',
    locations: [],
    geoExclusionsStr: '',
    perDealApproval: channel.per_deal_approval || '',
    whereToView: channel.where_to_view || '',
    graphicsURL: channel.graphics_url || '',
    lang: channel.lang || '',
    targetPackage: channel.target_package || '',
    whiteLogoURL: channel.white_logo_url || '',
    colorLogoURL: channel.color_logo_url || '',
    coppa: channel.coppa || 0,
    contentId: channel.content_id || '',
  }

  if (channel.logo) {
    processed.logo = channel.logo.blob
  }
  if (channel.watermark) {
    processed.watermark = {
      createTime: channel.watermark.create_time,
      updateTime: channel.watermark.update_time,
      position: channel.watermark.position ? channel.watermark.position : 'topLeft',
      size: channel.watermark.size ? channel.watermark.size : '200',
      state: channel.watermark.state,
      enabled: channel.watermark.enabled,
      transcodingUpdateRequired: channel.watermark.transcoding_update_required,
      pictureId: channel.watermark.picture_id,
    }
    processed.watermarkPictureId = channel.watermark.picture_id
    if (channel.watermark.picture) {
      processed.watermarkPicture = channel.watermark.picture.blob
    }
  }
  if (channel.xmltv) {
    processed.xmltv.lastImportTimeAuto = channel.xmltv.last_import_time_auto
    processed.xmltv.lastImportTimeCustom = channel.xmltv.last_import_time_custom
    processed.xmltv.resultURLAuto = channel.xmltv.url_auto
    processed.xmltv.resultURLCustom = channel.xmltv.url_custom
    processed.xmltv.userOutputURL = channel.xmltv.user_output_url
  }
  if (channel.timezone) {
    processed.timezone = processTimezone(channel.timezone)
  }
  if (channel.auto_scheduler_settings && channel.auto_scheduler_settings.as_depth_time_min) {
    // eslint-disable-next-line max-len
    processed.autoSchedulerDepth = Number(channel.auto_scheduler_settings.as_depth_time_min)
  }
  if (channel.ads) {
    processed.ads = {
      inContentAdvertising: channel.ads.in_content_advertising,
      adBlockDuration: channel.ads.ad_block_duration,
      minInterval: channel.ads.min_interval,
      programSafeZoneLeft: channel.ads.program_safe_zone_left,
      programSafeZoneRight: channel.ads.program_safe_zone_right,
    }
  }
  if (channel.instance) {
    processed.instance = processInstance(channel.instance)
  }
  if (channel.genres) {
    processed.genres = channel.genres.map((g) => processGenre(g))
  }
  if (channel.geoip_rules) {
    for (let i = 0; i < channel.geoip_rules.length; i += 1) {
      const rule = channel.geoip_rules[i]
      if (rule.subject_type === 'everyone') {
        processed.denyAll = rule.allow ? '0' : '1'
      } else if (rule.subject_type === 'location') {
        processed.locations.push({
          id: rule.location_id,
          name: '',
          code: '',
          type: '',
        })
      }
    }
  }
  return processed
}

export const processDistribution = (d) => {
  const processed = {
    id: d.id,
    name: d.name,
    createTime: d.create_time,
    updateTime: d.update_time,
    urls: {},
    whitelist: d.whitelist || [],
    channelId: d.channel_id,
  }
  for (const key in d.urls) {
    let streamURL = d.urls[key]
    streamURL = streamURL.replace('$origin', 'now')
    streamURL = streamURL.replace('$offset', '-1m_15s')
    streamURL = streamURL.replace(',', '_')
    processed.urls[key] = streamURL
  }
  return processed
}

export const processDistributionPackage = (p) => {
  const processed = {
    id: p.id,
    name: p.name,
    createTime: p.create_time,
    mtPrefix: p.mt_prefix || '',
    mtPrerollPrefix: p.mt_preroll_prefix || '',
    props: p.props || [],
    format: p.format || 'mediatailor',
  }
  return processed
}

export const processGenre = (g) => ({
  id: g.id,
  extId: g.ext_id,
  name: g.name,
})

export const processThirdPartyChannel = (channel) => {
  const processed = {
    id: channel.id,
    name: channel.name,
    shortDescription: channel.short_description,
    longDescription: channel.long_description,
    company: channel.company,
    url: channel.source_url,
    genres: channel.genres || [],
    rating: channel.rating || '',
    USPrivacy: channel.us_privacy || '',
    IABCategory: channel.iab_category || '',
    denyAll: '',
    locations: [],
    geoExclusionsStr: '',
    perDealApproval: channel.per_deal_approval || '',
    whereToView: channel.where_to_view || '',
    graphicsURL: channel.graphics_url || '',
    xmltvURL: channel.xmltv_url || '',
    lang: channel.lang || '',
    targetPackage: channel.target_package || '',
    coppa: channel.coppa || 0,
    contentId: channel.content_id || '',
    logoURL: channel.logo_url || '',
  }
  if (channel.genres) {
    processed.genres = channel.genres.map((g) => processGenre(g))
  }
  if (channel.geoip_rules) {
    for (let i = 0; i < channel.geoip_rules.length; i += 1) {
      const rule = channel.geoip_rules[i]
      if (rule.subject_type === 'everyone') {
        processed.denyAll = rule.allow ? '0' : '1'
      } else if (rule.subject_type === 'location') {
        processed.locations.push({
          id: rule.location_id,
          name: '',
          code: '',
          type: '',
        })
      }
    }
  }
  return processed
}

export const thirdPartyChannelToJsonAPI = (channel) => {
  const processed = {
    id: channel.id,
    name: channel.name,
    short_description: channel.shortDescription,
    long_description: channel.longDescription,
    company: channel.company,
    source_url: channel.url,
    genres: channel.genres || [],
  }
  return processed
}

export const processItem2 = (i) => {
  const processed = {
    id: i.id,
    type: i.type,
    name: i.name,
    startTime: i.start_time,
    endTime: i.end_time,
    assetResourceId: i.asset_resource_id || 0,
    assetResource: {},
    template_resource_id: i.template_resource_id || 0,
    templateResource: {},
    streamURL: '',
    duration: moment(i.end_time).utc().unix() * 1000 - moment(i.start_time).utc().unix() * 1000,
  }
  return processed
}

export const processProgram2 = (p) => {
  const processed = {
    id: p.id,
    type: p.type,
    name: p.name,
    startTime: p.start_time,
    endTime: p.end_time,
    assetId: p.asset_id || 0,
    items: [],
    fixed: p.fixed || false,
    pictureId: p.picture_id || 0,
    //
    subType: p.type,
    description: '',
    containsAd: false,
    containsBumper: false,
    tags: [],
  }
  if (p.items) {
    processed.items = p.items.map((i) => processItem2(i))
  }
  if (p.type === 'asset' && p.items) {
    for (let i = 0; i < p.items.length; i += 1) {
      const item = p.items[i]
      if (item.type === 'vod') {
        processed.subType = item.type
      }
      if (item.type === 'ad') {
        processed.containsAd = true
      }
    }
  }

  const start = moment(processed.startTime).utc().unix() * 1000
  const end = moment(processed.endTime).utc().unix() * 1000

  processed.duration = end - start
  return processed
}

export const TEMPLATE_TYPE = {
  BUMPER: 'bumper',
  ANNOUNCE: 'announce',
  BLACKOUT: 'blackout',
}

export const processTemplate = (t) => ({
  id: t.id,
  type: t.type,
  name: t.name,
  description: t.description,
  pictureURL: t.picture_url,
  streamURL: t.stream_url,
  settings: t.settings,
})

export const processSCOperator = (op) => {
  const processed = {
    id: op.id,
    status: op.status || '',
    extId: op.ext_id,
    distributionPackage: {},
    adminUsername: op.admin_username || '',
    scPackageId: op.sc_package_id || '',
  }
  if (op.distribution_package) {
    processed.distributionPackage = processDistributionPackage(op.distribution_package)
  }
  return processed
}

export const processSCAsset = (asset) => {
  const processed = {
    id: asset.id,
    catchupDepth: asset.catchup_depth || 0,
    name: asset.name || '',
    scChannelId: asset.sc_channel_id || 0,
    asset: asset.playout_asset || {},
    scAsset: asset.sc_asset || {},
    thirdPartyChannelId: asset.third_party_channel_id || 0,
    playoutChannelId: asset.playout_channel_id || 0,
    compareResult: asset.compare_result || [],
  }
  return processed
}
